import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaPen, FaPlus, FaTimes, FaEdit, FaEraser, FaTrash, FaPalette, FaSun, FaExpandArrowsAlt, FaRuler, FaHandPaper } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';
import { ThemeContext } from '../../context/ThemeContext';

// Custom hook for resize observer
const useResizeObserver = (ref, callback) => {
  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        callback(entry.contentRect);
      }
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, callback]);
};

export default function WhiteboardComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const { theme } = useContext(ThemeContext);
  const data = component.data || { tabs: [], active_tab: 0 };
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editTabName, setEditTabName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState({ points: [], color: '#000000', thickness: 2.0 });
  const [tool, setTool] = useState('pen'); // 'pen', 'eraser', or 'move'
  const [brushSize, setBrushSize] = useState(2.0);
  const [brushColor, setBrushColor] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [forceLightMode, setForceLightMode] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [showZoomMenu, setShowZoomMenu] = useState(false);
  const [toolbarWidth, setToolbarWidth] = useState(800);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [toolBeforeSpace, setToolBeforeSpace] = useState(null);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [toolBeforeShift, setToolBeforeShift] = useState(null);
  const [isErasing, setIsErasing] = useState(false);
  const [showEraserIcon, setShowEraserIcon] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const lastPanPointRef = useRef(null);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const eraserIconRef = useRef(null);
  const isMiddleMousePanningRef = useRef(false);

  // Monitor toolbar width
  useResizeObserver(toolbarRef, (rect) => {
    setToolbarWidth(rect.width);
  });

  // Handle keyboard events for space key and shift key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullscreen) return; // Only work in fullscreen mode
      
      // Space key for move tool
      if (e.code === 'Space' && !isSpacePressed && tool !== 'move') {
        e.preventDefault();
        setIsSpacePressed(true);
        setToolBeforeSpace(tool);
        setTool('move');
      }
      
      // Shift key for pen/eraser toggle
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        if (!isShiftPressed && (tool === 'pen' || tool === 'eraser')) {
          e.preventDefault();
          setIsShiftPressed(true);
          setToolBeforeShift(tool);
          setTool(tool === 'pen' ? 'eraser' : 'pen');
        }
      }
    };

    const handleKeyUp = (e) => {
      if (!isFullscreen) return; // Only work in fullscreen mode
      
      // Space key release
      if (e.code === 'Space' && isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(false);
        if (toolBeforeSpace) {
          setTool(toolBeforeSpace);
          setToolBeforeSpace(null);
        }
      }
      
      // Shift key release
      if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
        if (isShiftPressed) {
          e.preventDefault();
          setIsShiftPressed(false);
          if (toolBeforeShift) {
            setTool(toolBeforeShift);
            setToolBeforeShift(null);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpacePressed, tool, toolBeforeSpace, isShiftPressed, toolBeforeShift, isFullscreen]);

  // Handle eraser icon visibility when tool changes
  useEffect(() => {
    if (tool === 'eraser') {
      // Only show if mouse is over canvas
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        // Check if mouse is currently over the canvas
        const handleMouseCheck = () => setShowEraserIcon(true);
        canvas.addEventListener('mouseenter', handleMouseCheck);
        return () => canvas.removeEventListener('mouseenter', handleMouseCheck);
      }
    } else {
      setShowEraserIcon(false);
    }
  }, [tool]);

  // Determine if we should show inline sliders or icon menus
  const showInlineSliders = toolbarWidth > 600 || isFullscreen;

  // Color adaptation function
  const adaptColorForTheme = (color, isCurrentlyDark, shouldBeDark) => {
    // Handle the monochrome color that adapts to theme
    if (color === '#000000') {
      // In dark theme (or when shouldBeDark is true), show white
      // In light theme (or when shouldBeDark is false), show black
      return shouldBeDark ? '#ffffff' : '#000000';
    }
    
    // For other colors, apply intelligent adaptation only if theme actually changed
    if (isCurrentlyDark === shouldBeDark) return color;
    
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate brightness (0-255)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // If it's very dark (< 50) and we need light theme, make it lighter
    // If it's very light (> 200) and we need dark theme, make it darker
    if (shouldBeDark && brightness > 200) {
      // Make lighter colors darker for dark theme
      const factor = 0.3; // darken by 70%
      return `#${Math.round(r * factor).toString(16).padStart(2, '0')}${Math.round(g * factor).toString(16).padStart(2, '0')}${Math.round(b * factor).toString(16).padStart(2, '0')}`;
    } else if (!shouldBeDark && brightness < 50) {
      // Make darker colors lighter for light theme
      const factor = 0.8; // lighten significantly
      const newR = Math.min(255, Math.round(r + (255 - r) * factor));
      const newG = Math.min(255, Math.round(g + (255 - g) * factor));
      const newB = Math.min(255, Math.round(b + (255 - b) * factor));
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    
    // For medium brightness colors, return as-is
    return color;
  };

  const colors = [
    '#ef4444', '#10b981', '#3b82f6', '#f59e0b', 
    '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899',
    '#6b7280', '#000000' // Single monochrome color that adapts to theme
  ];

  // Directly compute display colors based on theme and force light mode
  const displayColors = React.useMemo(() => {
    return colors.map(color => {
      if (color === '#000000') {
        // If force light mode is on, always show black (for light canvas)
        // Otherwise, follow the theme (white in dark, black in light)
        if (theme === 'dark' && forceLightMode) {
          return '#000000'; // Show black for light canvas
        } else {
          return theme === 'dark' ? '#ffffff' : '#000000';
        }
      }
      return color;
    });
  }, [theme, forceLightMode]);

  // Directly compute preview color based on theme, force light mode, and brush color
  const previewColor = React.useMemo(() => {
    if (brushColor === '#000000') {
      // If force light mode is on, always show black (for light canvas)
      // Otherwise, follow the theme (white in dark, black in light)
      if (theme === 'dark' && forceLightMode) {
        return '#000000'; // Show black for light canvas
      } else {
        return theme === 'dark' ? '#ffffff' : '#000000';
      }
    }
    return brushColor;
  }, [brushColor, theme, forceLightMode]);

  // Sync canvas when active tab changes or theme changes
  useEffect(() => {
    redrawCanvas();
  }, [data.active_tab, data.tabs, theme, forceLightMode, canvasOffset, zoom]);

  // Set up canvas dimensions
  useEffect(() => {
    if (canvasRef.current && containerRef.current) {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      // Reset canvas offset when transitioning to fullscreen for clean positioning
      if (isFullscreen) {
        setCanvasOffset({ x: 0, y: 0 });
      }
      
      // Resize canvas to match container
      const resizeCanvas = () => {
        // Longer delay to ensure layout is fully settled, especially important for fullscreen transitions
        setTimeout(() => {
          const rect = container.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
          redrawCanvas();
          
          // Force a second adjustment after a brief delay to catch any remaining layout shifts
          setTimeout(() => {
            const newRect = container.getBoundingClientRect();
            if (newRect.width !== canvas.width || newRect.height !== canvas.height) {
              canvas.width = newRect.width;
              canvas.height = newRect.height;
              redrawCanvas();
            }
          }, 50);
        }, 50);
      };
      
      resizeCanvas();
      
      // Add resize observer to handle container size changes
      const resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(container);
      
      return () => resizeObserver.disconnect();
    }
  }, [isFullscreen]);

  const redrawCanvas = () => {
    if (!canvasRef.current || !data.tabs[data.active_tab]) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save current transform
    ctx.save();
    
    // Apply canvas offset and zoom
    ctx.translate(canvasOffset.x, canvasOffset.y);
    ctx.scale(zoom, zoom);
    
    // Determine if we should use dark theme colors
    const shouldUseDarkTheme = theme === 'dark' && !forceLightMode;
    
    // Draw all strokes
    const strokes = data.tabs[data.active_tab]?.strokes || [];
    strokes.forEach(stroke => {
      if (stroke.points.length < 4) return;
      
      // Adapt stroke color based on current theme and force light mode setting
      const adaptedColor = adaptColorForTheme(stroke.color, theme === 'dark', shouldUseDarkTheme);
      
      ctx.strokeStyle = adaptedColor;
      ctx.lineWidth = stroke.thickness;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      ctx.moveTo(stroke.points[0], stroke.points[1]);
      
      for (let i = 2; i < stroke.points.length; i += 2) {
        ctx.lineTo(stroke.points[i], stroke.points[i + 1]);
      }
      
      ctx.stroke();
    });
    
    // Restore transform
    ctx.restore();
  };

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate position relative to canvas, accounting for any scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    return {
      x: (x - canvasOffset.x) / zoom,
      y: (y - canvasOffset.y) / zoom
    };
  };

  const getTouchPos = (e) => {
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    
    // Calculate position relative to canvas, accounting for any scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (touch.clientX - rect.left) * scaleX;
    const y = (touch.clientY - rect.top) * scaleY;
    
    return {
      x: (x - canvasOffset.x) / zoom,
      y: (y - canvasOffset.y) / zoom
    };
  };

  const getScreenPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const getTouchScreenPos = (e) => {
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  const startDrawing = (pos) => {
    if (tool === 'move') {
      const screenPos = lastPanPointRef.current;
      setIsPanning(true);
      lastPanPointRef.current = screenPos;
      return;
    }
    
    if (tool === 'eraser') {
      setIsErasing(true);
      eraseAt(pos);
      return;
    }
    
    isDrawingRef.current = true;
    setIsDrawing(true);
    lastPointRef.current = pos;
    
    setCurrentStroke({
      points: [pos.x, pos.y],
      color: brushColor,
      thickness: brushSize
    });
  };

  const draw = (pos, screenPos) => {
    if (tool === 'move' && isPanning && lastPanPointRef.current) {
      const deltaX = screenPos.x - lastPanPointRef.current.x;
      const deltaY = screenPos.y - lastPanPointRef.current.y;
      
      setCanvasOffset(prev => ({
        x: prev.x + deltaX / zoom,
        y: prev.y + deltaY / zoom
      }));
      
      lastPanPointRef.current = screenPos;
      return;
    }
    
    if (tool === 'eraser' && isErasing) {
      eraseAt(pos);
      return;
    }
    
    if (!isDrawingRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Save current transform
    ctx.save();
    
    // Apply canvas offset and zoom
    ctx.translate(canvasOffset.x, canvasOffset.y);
    ctx.scale(zoom, zoom);
    
    // Determine if we should use dark theme colors
    const shouldUseDarkTheme = theme === 'dark' && !forceLightMode;
    const adaptedColor = adaptColorForTheme(brushColor, theme === 'dark', shouldUseDarkTheme);
    
    // Draw on canvas immediately with adapted color
    ctx.strokeStyle = adaptedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    // Restore transform
    ctx.restore();
    
    // Update current stroke
    setCurrentStroke(prev => ({
      ...prev,
      points: [...prev.points, pos.x, pos.y]
    }));
    
    lastPointRef.current = pos;
  };

  const stopDrawing = () => {
    if (tool === 'move') {
      setIsPanning(false);
      lastPanPointRef.current = null;
      return;
    }
    
    if (tool === 'eraser') {
      setIsErasing(false);
      return;
    }
    
    if (!isDrawingRef.current) {
      isDrawingRef.current = false;
      setIsDrawing(false);
      return;
    }
    
    isDrawingRef.current = false;
    setIsDrawing(false);
    
    // Save the stroke
    if (currentStroke.points.length >= 4) {
      const updatedTabs = [...data.tabs];
      const strokeToSave = {
        ...currentStroke,
        created_at: new Date().toISOString()
      };
      
      updatedTabs[data.active_tab].strokes.push(strokeToSave);
      
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
    }
    
    setCurrentStroke({ points: [], color: brushColor, thickness: brushSize });
  };

  const eraseAt = (pos) => {
    const updatedTabs = [...data.tabs];
    const strokes = updatedTabs[data.active_tab].strokes;
    
    // Find strokes that intersect with eraser position
    const newStrokes = strokes.filter(stroke => {
      for (let i = 0; i < stroke.points.length - 2; i += 2) {
        const x1 = stroke.points[i];
        const y1 = stroke.points[i + 1];
        const x2 = stroke.points[i + 2];
        const y2 = stroke.points[i + 3];
        
        // Check if eraser position is near this line segment
        const distance = distanceToLineSegment(pos.x, pos.y, x1, y1, x2, y2);
        if (distance < brushSize + stroke.thickness) {
          return false; // Remove this stroke
        }
      }
      return true; // Keep this stroke
    });
    
    if (newStrokes.length !== strokes.length) {
      updatedTabs[data.active_tab].strokes = newStrokes;
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      redrawCanvas();
    }
  };

  // Distance from point to line segment
  const distanceToLineSegment = (px, py, x1, y1, x2, y2) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return Math.sqrt(A * A + B * B);
    
    let param = dot / lenSq;
    param = Math.max(0, Math.min(1, param));
    
    const xx = x1 + param * C;
    const yy = y1 + param * D;
    
    const dx = px - xx;
    const dy = py - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    // Handle middle mouse button for panning (button 1)
    if (e.button === 1) {
      e.preventDefault();
      const screenPos = getScreenPos(e);
      lastPanPointRef.current = screenPos;
      isMiddleMousePanningRef.current = true;
      setIsPanning(true);
      return;
    }
    
    // Handle right mouse button for erasing when pen is active (button 2)
    if (e.button === 2 && tool === 'pen') {
      e.preventDefault();
      const pos = getMousePos(e);
      eraseAt(pos);
      return;
    }
    
    // Handle left mouse button (button 0)
    if (e.button !== 0) return;
    
    e.preventDefault();
    const pos = getMousePos(e);
    const screenPos = getScreenPos(e);
    lastPanPointRef.current = screenPos;
    startDrawing(pos);
  };

  const handleMouseMove = (e) => {
    e.preventDefault();
    const pos = getMousePos(e);
    const screenPos = getScreenPos(e);
    
    // Handle middle mouse panning
    if (isMiddleMousePanningRef.current && isPanning && lastPanPointRef.current) {
      const deltaX = screenPos.x - lastPanPointRef.current.x;
      const deltaY = screenPos.y - lastPanPointRef.current.y;
      
      setCanvasOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      lastPanPointRef.current = screenPos;
      return;
    }
    
    // Update mouse position for eraser icon using direct DOM manipulation
    if (tool === 'eraser' && eraserIconRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Direct DOM update for better performance
      eraserIconRef.current.style.left = `${x + 10}px`;
      eraserIconRef.current.style.top = `${y - 10}px`;
      eraserIconRef.current.style.transform = 'translate(0, -100%)';
    }
    
    draw(pos, screenPos);
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    
    // Handle middle mouse button release
    if (e.button === 1 && isMiddleMousePanningRef.current) {
      isMiddleMousePanningRef.current = false;
      setIsPanning(false);
      lastPanPointRef.current = null;
      return;
    }
    
    stopDrawing();
  };

  const handleMouseEnter = (e) => {
    if (tool === 'eraser') {
      setShowEraserIcon(true);
    }
  };

  const handleMouseLeave = (e) => {
    setShowEraserIcon(false);
  };

  const handleContextMenu = (e) => {
    // Prevent context menu when pen tool is active (for right-click erasing)
    if (tool === 'pen') {
      e.preventDefault();
      return false;
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    const screenPos = getTouchScreenPos(e);
    lastPanPointRef.current = screenPos;
    startDrawing(pos);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const pos = getTouchPos(e);
    const screenPos = getTouchScreenPos(e);
    draw(pos, screenPos);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  // Handle wheel event for zooming
  const handleWheel = (e) => {
    if (!isFullscreen) return; // Only work in fullscreen mode
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.25 : 0.25;
    const newZoom = Math.min(3, Math.max(0.25, zoom + delta));
    setZoom(newZoom);
  };

  const addTab = () => {
    const newTabs = [
      ...data.tabs,
      {
        name: `Drawing ${data.tabs.length + 1}`,
        strokes: [],
        created_at: new Date().toISOString()
      }
    ];
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: newTabs.length - 1
      }
    });
  };

  const deleteTab = (index) => {
    const newTabs = data.tabs.filter((_, i) => i !== index);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: Math.min(data.active_tab, newTabs.length - 1)
      }
    });
    setDeleteConfirm(null);
  };

  const startEditingTab = (index) => {
    setEditingTabIndex(index);
    setEditTabName(data.tabs[index].name);
  };

  const saveTabName = () => {
    if (editTabName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[editingTabIndex].name = editTabName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingTabIndex(null);
    }
  };

  const clearCanvas = () => {
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].strokes = [];
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
    redrawCanvas();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="tab-bar flex items-center gap-1 mb-4">
        <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
          {data.tabs.map((tab, index) => (
            <div
              key={index}
              className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors text-sm ${
                index === data.active_tab 
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
              }`}
              onClick={() => onUpdate({
                ...component,
                data: {
                  ...data,
                  active_tab: index
                }
              })}
            >
              {editingTabIndex === index ? (
                <input
                  type="text"
                  className="px-1 bg-transparent rounded focus:outline-none w-20"
                  value={editTabName}
                  onChange={(e) => setEditTabName(e.target.value)}
                  onBlur={saveTabName}
                  onKeyPress={(e) => e.key === 'Enter' && saveTabName()}
                  autoFocus
                />
              ) : (
                <>
                  <span className={`truncate ${isFullscreen ? 'max-w-[200px]' : 'max-w-[120px]'}`} title={tab.name}>{tab.name}</span>
                  <button 
                    className="p-1 hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditingTab(index);
                    }}
                  >
                    <FaEdit size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                  </button>
                </>
              )}
              
              {data.tabs.length > 1 && (
                <button
                  className="p-1 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm({ index, tabName: tab.name });
                  }}
                >
                  <FaTimes size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                </button>
              )}
            </div>
          ))}
          
          <button
            className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm"
            onClick={addTab}
            title="Add new drawing"
          >
            <FaPlus size={12} />
          </button>
        </div>
      </div>
      
      {/* Drawing toolbar - separate row below tabs */}
      {data.tabs.length > 0 && (
        <div ref={toolbarRef} className="mb-4 p-3 bg-[var(--bg-primary)] rounded-lg">
          {showInlineSliders ? (
            // Full toolbar with inline sliders
            <div className="flex items-center justify-between gap-4">
              {/* Tools section */}
              <div className="flex items-center gap-2">
                <button
                  className={`px-2 py-1 rounded-md transition-colors text-sm flex items-center gap-1 ${
                    tool === 'pen' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  }`}
                  onClick={() => setTool('pen')}
                  title="Pen tool"
                >
                  <FaPen size={12} />
                </button>
                
                <button
                  className={`px-2 py-1 rounded-md transition-colors text-sm flex items-center gap-1 ${
                    tool === 'eraser' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  }`}
                  onClick={() => setTool('eraser')}
                  title="Eraser tool"
                >
                  <FaEraser size={12} />
                </button>

                <button
                  className={`px-2 py-1 rounded-md transition-colors text-sm flex items-center gap-1 ${
                    tool === 'move' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  } ${tool === 'move' ? 'cursor-grab' : 'cursor-pointer'}`}
                  onClick={() => setTool('move')}
                  title="Move tool"
                >
                  <FaHandPaper size={12} />
                </button>

                <div className="relative">
                  <button
                    className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm flex items-center gap-1"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    title="Color picker"
                  >
                    <FaPalette size={12} />
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: previewColor }}
                    />
                  </button>
                  
                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 p-3 bg-[var(--bg-secondary)] rounded-lg shadow-lg z-10">
                      <div className="grid grid-cols-4 gap-1">
                        {displayColors.map((displayColor, index) => {
                          const originalColor = colors[index];
                          return (
                            <button
                              key={originalColor}
                              className={`w-8 h-8 rounded border-2 transition-all ${
                                brushColor === originalColor ? 'border-[var(--primary)] scale-110' : 'border-[var(--bg-primary)] hover:scale-105'
                              }`}
                              style={{ backgroundColor: displayColor }}
                              onClick={() => {
                                setBrushColor(originalColor);
                                setShowColorPicker(false);
                              }}
                              title={originalColor}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sliders section */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[var(--text-secondary)] min-w-[35px]">Size:</label>
                  <input
                    type="range"
                    min="0.5"
                    max="20"
                    step="0.5"
                    value={brushSize}
                    onChange={(e) => setBrushSize(parseFloat(e.target.value))}
                    className="w-24 accent-[var(--primary)]"
                    style={{
                      background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((brushSize - 0.5) / (20 - 0.5)) * 100}%, var(--bg-hover) ${((brushSize - 0.5) / (20 - 0.5)) * 100}%, var(--bg-hover) 100%)`
                    }}
                  />
                  <span className="text-xs text-[var(--text-secondary)] w-8">{brushSize}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[var(--text-secondary)] min-w-[35px]">Zoom:</label>
                  <input
                    type="range"
                    min="0.25"
                    max="3"
                    step="0.25"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-24 accent-[var(--primary)]"
                    style={{
                      background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((zoom - 0.25) / (3 - 0.25)) * 100}%, var(--bg-hover) ${((zoom - 0.25) / (3 - 0.25)) * 100}%, var(--bg-hover) 100%)`
                    }}
                  />
                  <span className="text-xs text-[var(--text-secondary)] w-8">{zoom}x</span>
                </div>
              </div>

              {/* Action buttons section */}
              <div className="flex items-center gap-2">
                {/* Light mode toggle - only visible in dark mode */}
                {theme === 'dark' && (
                  <button
                    className={`px-2 py-1 rounded-md transition-colors text-sm flex items-center gap-1 ${
                      forceLightMode
                        ? 'bg-[var(--primary)] text-white'
                        : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                    }`}
                    onClick={() => setForceLightMode(!forceLightMode)}
                    title={forceLightMode ? "Use dark canvas background" : "Force light canvas background"}
                  >
                    <FaSun size={12} />
                  </button>
                )}

                <button
                  className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-red-500 hover:text-white transition-colors text-sm flex items-center gap-1"
                  onClick={clearCanvas}
                  title="Clear canvas"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            </div>
          ) : (
            // Compact toolbar with icon menus
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  className={`p-1 rounded-md transition-colors text-sm ${
                    tool === 'pen' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  }`}
                  onClick={() => setTool('pen')}
                  title="Pen tool"
                >
                  <FaPen size={11} />
                </button>
                
                <button
                  className={`p-1 rounded-md transition-colors text-sm ${
                    tool === 'eraser' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  }`}
                  onClick={() => setTool('eraser')}
                  title="Eraser tool"
                >
                  <FaEraser size={11} />
                </button>

                <button
                  className={`p-1 rounded-md transition-colors text-sm ${
                    tool === 'move' 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                  } ${tool === 'move' ? 'cursor-grab' : 'cursor-pointer'}`}
                  onClick={() => setTool('move')}
                  title="Move tool"
                >
                  <FaHandPaper size={11} />
                </button>

                <div className="relative">
                  <button
                    className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm flex items-center gap-1"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    title="Color picker"
                  >
                    <FaPalette size={11} />
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: previewColor }}
                    />
                  </button>
                  
                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 p-3 bg-[var(--bg-secondary)] rounded-lg shadow-lg z-10 min-w-[160px]">
                      <div className="grid grid-cols-4 gap-2">
                        {displayColors.map((displayColor, index) => {
                          const originalColor = colors[index];
                          return (
                            <button
                              key={originalColor}
                              className={`w-7 h-7 rounded border-2 transition-all ${
                                brushColor === originalColor ? 'border-[var(--primary)] scale-110' : 'border-[var(--bg-primary)] hover:scale-105'
                              }`}
                              style={{ backgroundColor: displayColor }}
                              onClick={() => {
                                setBrushColor(originalColor);
                                setShowColorPicker(false);
                              }}
                              title={originalColor}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <button
                    className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs flex items-center gap-1 min-w-[60px] justify-center"
                    onClick={() => setShowSizeMenu(!showSizeMenu)}
                    title={`Brush size: ${brushSize}`}
                  >
                    <FaRuler size={10} />
                    <span className="text-xs">{brushSize}</span>
                  </button>
                  
                  {showSizeMenu && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 p-2 bg-[var(--bg-secondary)] rounded-lg shadow-lg z-10 w-[140px]">
                      <div className="flex flex-col gap-1 items-center">
                        <label className="text-xs text-[var(--text-secondary)] text-center">Size: {brushSize}</label>
                        <input
                          type="range"
                          min="0.5"
                          max="20"
                          step="0.5"
                          value={brushSize}
                          onChange={(e) => setBrushSize(parseFloat(e.target.value))}
                          className="w-full accent-[var(--primary)]"
                          style={{
                            background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((brushSize - 0.5) / (20 - 0.5)) * 100}%, var(--bg-hover) ${((brushSize - 0.5) / (20 - 0.5)) * 100}%, var(--bg-hover) 100%)`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <button
                    className="px-2 py-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-xs flex items-center gap-1 min-w-[60px] justify-center"
                    onClick={() => setShowZoomMenu(!showZoomMenu)}
                    title={`Zoom: ${zoom}x`}
                  >
                    <FaExpandArrowsAlt size={10} />
                    <span className="text-xs">{zoom}x</span>
                  </button>
                  
                  {showZoomMenu && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 p-2 bg-[var(--bg-secondary)] rounded-lg shadow-lg z-10 w-[140px]">
                      <div className="flex flex-col gap-1 items-center">
                        <label className="text-xs text-[var(--text-secondary)] text-center">Zoom: {zoom}x</label>
                        <input
                          type="range"
                          min="0.25"
                          max="3"
                          step="0.25"
                          value={zoom}
                          onChange={(e) => setZoom(parseFloat(e.target.value))}
                          className="w-full accent-[var(--primary)]"
                          style={{
                            background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((zoom - 0.25) / (3 - 0.25)) * 100}%, var(--bg-hover) ${((zoom - 0.25) / (3 - 0.25)) * 100}%, var(--bg-hover) 100%)`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 justify-center">
                {theme === 'dark' && (
                  <button
                    className={`p-1 rounded-md transition-colors text-sm ${
                      forceLightMode
                        ? 'bg-[var(--primary)] text-white'
                        : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
                    }`}
                    onClick={() => setForceLightMode(!forceLightMode)}
                    title={forceLightMode ? "Use dark canvas background" : "Force light canvas background"}
                  >
                    <FaSun size={11} />
                  </button>
                )}

                <button
                  className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-red-500 hover:text-white transition-colors text-sm"
                  onClick={clearCanvas}
                  title="Clear canvas"
                >
                  <FaTrash size={11} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto">
            <p className="mb-4">Are you sure you want to delete "{deleteConfirm.tabName}"?</p>
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)]"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
                onClick={() => deleteTab(deleteConfirm.index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Canvas */}
      {data.tabs.length > 0 ? (
        <div 
          ref={containerRef}
          className={`w-full rounded-lg flex-1 min-h-0 relative`}
          style={{
            backgroundColor: forceLightMode ? '#f9fafb' : 'var(--bg-secondary)'
          }}
        >
          <canvas
            ref={canvasRef}
            className="block w-full h-full"
            style={{
              backgroundColor: forceLightMode ? '#ffffff' : 'var(--bg-primary)',
              touchAction: 'none',
              cursor: tool === 'move' ? (isPanning ? 'grabbing' : 'grab') : 'crosshair'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onContextMenu={handleContextMenu}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
          />
          
          {/* Floating eraser icon */}
          {tool === 'eraser' && showEraserIcon && (
            <div
              ref={eraserIconRef}
              className="absolute pointer-events-none z-10"
              style={{
                left: 0,
                top: 0,
                transform: 'translate(0, -100%)'
              }}
            >
              <div className="text-white p-1 rounded flex items-center justify-center" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }}>
                <FaEraser size={12} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div 
          className={`w-full rounded-lg flex items-center justify-center flex-1 min-h-0`}
          style={{
            backgroundColor: forceLightMode ? '#ffffff' : 'var(--bg-primary)'
          }}
        >
          <p className="text-[var(--text-secondary)] italic">Click the + button to create your first drawing</p>
        </div>
      )}
    </div>
  );
}
